import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Input from "../../../common/element/Input/Input";
import { changePassword, clearChangePassword } from "../../../redux/Setting/Settingsslice";
import { logout } from "../../../redux/auth/authSlice";
import {
  selectChangePasswordLoading,
  selectChangePasswordError,
  selectChangePasswordData,
} from "../../../redux/Setting/Settingsselectors";
import SweetAlertService from "../../../common/SweetAlert/SweetAlert";

const SecuritySettings = () => {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdError, setPwdError] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectChangePasswordLoading);
  const error = useSelector(selectChangePasswordError);
  const successData = useSelector(selectChangePasswordData);

  useEffect(() => {
    // Clear change password state on mount
    dispatch(clearChangePassword());
    return () => {
      dispatch(clearChangePassword());
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (successData && !error) {
      SweetAlertService.showSuccess({
        title: "Password Changed!",
        message: "Your password has been changed successfully. You will be logged out.",
        confirmButtonText: "OK",
        onConfirm: () => {
          localStorage.clear();
          dispatch(logout());
          navigate("/login");
        },
        allowClose: false
      });
    } else if (error) {
      SweetAlertService.showError({
        title: "Error!",
        message: error,
        confirmButtonText: "OK",
        allowClose: true
      });
    }
    // eslint-disable-next-line
  }, [successData, error]);

  // Eye visibility states
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const conditions = {
    length: newPwd.length >= 8,
    lower: /[a-z]/.test(newPwd),
    upper: /[A-Z]/.test(newPwd),
    number: /[0-9]/.test(newPwd),
  };

  const handleSave = () => {
    if (newPwd !== confirmPwd) {
      setPwdError("Passwords do not match");
      return;
    }
    setPwdError("");
    dispatch(
      changePassword({
        old_password: currentPwd,
        new_password: newPwd,
      })
    );
  };

  const handleCancel = () => {
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
    setPwdError("");
  };

  // Helper function to safely extract value whether it's an Event or a direct Value
  const getValue = (e) => {
    return e?.target ? e.target.value : e;
  };

  const EyeIcon = ({ visible, onClick }) => (
    <span
      onClick={onClick}
      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
    >
      {visible ? (




  <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            d="M1 10C1 10 4.5 3 10 3C15.5 3 19 10 19 10C19 10 15.5 17 10 17C4.5 17 1 10 1 10Z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 13C11.6569 13 13 11.6569 13 10C13 8.34315 11.6569 7 10 7C8.34315 7 7 8.34315 7 10C7 11.6569 8.34315 13 10 13Z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg> 
     
      ) : (
         <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            d="M17.94 17.94L2 2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.53 9.53C9.07327 9.98673 8.4538 10.2433 7.80785 10.2433C7.1619 10.2433 6.54243 9.98673 6.0857 9.53C5.62897 9.07327 5.37239 8.4538 5.37239 7.80785C5.37239 7.1619 5.62897 6.54243 6.0857 6.0857"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.19 12.19C14.07 11.45 15.65 10.05 16.5 8.18C15.22 5.43 12.24 3.5 8.87 3.5C7.94 3.5 7.04 3.65 6.2 3.92"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );

  return (
    <div className="w-full bg-white">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h2 className="font-lato font-medium text-[16px] text-[#101828] mb-1 text-start">
            Security
          </h2>
          <p className="font-lato text-[14px] text-[#475467] text-start">
            Manage and personalize your security settings for enhanced account
            protection and data privacy.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded-lg bg-[#FFF4E5] text-[#344054] text-sm font-medium hover:bg-orange-100 transition"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-[#FF8E1C] text-white text-sm font-medium hover:bg-[#e67e16] transition"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

   
      {/* Success message handled by SweetAlertService and logout */}

      {/* Body: Grid Layout */}
      <div className="grid grid-cols-12 gap-8 border-t border-[#EAECF0] pt-8">
        {/* LEFT: Inputs (Span 8) */}
        <div className="col-span-8 flex flex-col gap-6">
          {/* Current Password */}
          <div className="grid grid-cols-12 gap-4 items-center">
            <label className="col-span-4 font-lato text-[14px] font-medium text-[#344054] text-start">
              Current password
            </label>
            <div className="col-span-8 relative">
              <Input
                type={showCurrent ? "text" : "password"}
                placeholder="•••••••••"
                value={currentPwd}
                inputchangehandler={(e) => setCurrentPwd(getValue(e))}
                className="w-full pr-10"
              />
              <EyeIcon
                visible={showCurrent}
                onClick={() => setShowCurrent(!showCurrent)}
              />
            </div>
          </div>

          {/* New Password */}
          <div className="grid grid-cols-12 gap-4 items-center">
            <label className="col-span-4 font-lato text-[14px] font-medium text-[#344054] text-start">
              New password
            </label>
            <div className="col-span-8 relative">
              <Input
                type={showNew ? "text" : "password"}
                placeholder="•••••••••"
                value={newPwd}
                inputchangehandler={(e) => {
                  const val = getValue(e);
                  setNewPwd(val);
                  if (confirmPwd && val !== confirmPwd) {
                    setPwdError("Passwords do not match");
                  } else {
                    setPwdError("");
                  }
                }}
                className="w-full pr-10"
              />
              <EyeIcon
                visible={showNew}
                onClick={() => setShowNew(!showNew)}
              />
            </div>
          </div>

          {/* Confirm Password */}
          <div className="grid grid-cols-12 gap-4 items-center">
            <label className="col-span-4 font-lato text-[14px] font-medium text-[#344054] text-start">
              Confirm password
            </label>
            <div className="col-span-8 relative">
              <Input
                type={showConfirm ? "text" : "password"}
                placeholder="•••••••••"
                value={confirmPwd}
                inputchangehandler={(e) => {
                  const val = getValue(e);
                  setConfirmPwd(val);
                  if (newPwd && val !== newPwd) {
                    setPwdError("Passwords do not match");
                  } else {
                    setPwdError("");
                  }
                }}
                className="w-full pr-10"
              />
              <EyeIcon
                visible={showConfirm}
                onClick={() => setShowConfirm(!showConfirm)}
              />
              {pwdError && (
                <p className="text-[12px] text-red-600 mt-1 absolute -bottom-5 left-0">
                  {pwdError}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Rules (Span 4) */}
        <div className="col-span-4">
          <div className="w-full bg-[#FFF4E5] rounded-lg p-5">
            <p className="font-lato text-[14px] font-medium text-[#344054] mb-3 text-start">
              Your password must contain:
            </p>

            <div className="space-y-2">
              {[
                { label: "at least 8 Characters", key: "length" },
                { label: "lower case letters (i.e a-z)", key: "lower" },
                { label: "upper case letters (i.e A-Z)", key: "upper" },
                { label: "numbers (i.e 0-9)", key: "number" },
              ].map((rule, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center
                      ${
                        conditions[rule.key]
                          ? "bg-[#FF8E1C] border-[#FF8E1C]"
                          : "bg-white border-gray-300"
                      }`}
                  >
                    {conditions[rule.key] && (
                      <svg
                        width="10"
                        height="8"
                        viewBox="0 0 10 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 1L3.5 6.5L1 4"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="font-lato text-[13px] text-[#344054]">
                    {rule.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;